import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import StickyQuoteBar from '../../components/feature/StickyQuoteBar';
import { useEffect } from 'react';

export default function BestDSCRLendersPage() {
  const [ref1, inView1] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [ref2, inView2] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    document.title = 'Best DSCR Lenders 2026: Top DSCR Loan Companies & Private Lenders';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Compare the best DSCR lenders and top DSCR loan companies nationwide. Find private lenders, national DSCR loan providers, and best DSCR loan lenders with competitive rates.');
    }
  }, []);

  const topLenders = [
    {
      name: 'Visio Lending',
      rating: 4.8,
      minDSCR: '0.75',
      minCredit: '660',
      maxLTV: '80%',
      rate: '7.25%',
      features: ['No ratio DSCR loans', 'Nationwide coverage', 'Fast closing', 'Portfolio loans'],
      specialty: 'Best for experienced investors',
    },
    {
      name: 'Kiavi',
      rating: 4.7,
      minDSCR: '1.0',
      minCredit: '660',
      maxLTV: '80%',
      rate: '7.50%',
      features: ['Fix and flip', 'Bridge loans', 'DSCR loans', 'Tech-enabled platform'],
      specialty: 'Best for tech-savvy investors',
    },
    {
      name: 'Lima One Capital',
      rating: 4.6,
      minDSCR: '1.0',
      minCredit: '680',
      maxLTV: '80%',
      rate: '7.75%',
      features: ['Rental loans', 'Bridge financing', 'New construction', 'Multifamily'],
      specialty: 'Best for multifamily properties',
    },
    {
      name: 'Anchor Loans',
      rating: 4.7,
      minDSCR: '1.0',
      minCredit: '640',
      maxLTV: '75%',
      rate: '7.99%',
      features: ['Fast approval', 'Flexible terms', 'Short-term rentals', 'Nationwide'],
      specialty: 'Best for short-term rental investors',
    },
    {
      name: 'RCN Capital',
      rating: 4.5,
      minDSCR: '1.0',
      minCredit: '660',
      maxLTV: '80%',
      rate: '8.25%',
      features: ['Rental loans', 'Fix and flip', 'Ground-up construction', 'Portfolio'],
      specialty: 'Best for diverse loan products',
    },
    {
      name: 'CoreVest Finance',
      rating: 4.6,
      minDSCR: '1.0',
      minCredit: '680',
      maxLTV: '80%',
      rate: '7.85%',
      features: ['Single-family rentals', 'Portfolio loans', 'Competitive rates', 'National'],
      specialty: 'Best for portfolio investors',
    },
  ];

  const lenderTypes = [
    {
      icon: 'ri-bank-line',
      title: 'National DSCR Lenders',
      description: 'Large-scale lenders offering DSCR loans nationwide with standardized programs, competitive rates, and streamlined processes.',
      pros: ['Nationwide coverage', 'Competitive rates', 'Established reputation', 'Multiple loan products'],
    },
    {
      icon: 'ri-user-star-line',
      title: 'Private DSCR Lenders',
      description: 'Private money lenders specializing in DSCR loans with flexible underwriting and faster approval for unique situations.',
      pros: ['Flexible terms', 'Fast approval', 'Creative solutions', 'Relationship-based'],
    },
    {
      icon: 'ri-community-line',
      title: 'Regional DSCR Lenders',
      description: 'Local and regional lenders with deep market knowledge and personalized service for specific geographic areas.',
      pros: ['Local expertise', 'Personal service', 'Market knowledge', 'Flexible underwriting'],
    },
  ];

  const selectionCriteria = [
    {
      icon: 'ri-percent-line',
      title: 'Interest Rates',
      description: 'Compare DSCR loan rates across lenders. Rates typically range from 7-10% depending on credit, DSCR ratio, and property type.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Loan Terms',
      description: 'Evaluate loan terms including 30-year fixed, 5/1 ARM, interest-only options, and prepayment penalties.',
    },
    {
      icon: 'ri-time-line',
      title: 'Closing Speed',
      description: 'Consider closing timelines. Top DSCR lenders can close in 2-3 weeks with streamlined underwriting.',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Loan Programs',
      description: 'Review available programs: standard DSCR, no ratio DSCR, cash-out refinance, and portfolio loans.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Customer Service',
      description: 'Assess lender responsiveness, dedicated loan officers, and investor-focused support teams.',
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Geographic Coverage',
      description: 'Verify lender operates in your target markets. Some lenders have state restrictions.',
    },
  ];

  const faqs = [
    {
      question: 'Who are the best DSCR lenders in 2026?',
      answer: 'The best DSCR lenders in 2026 include Visio Lending, Kiavi, Lima One Capital, Anchor Loans, RCN Capital, and CoreVest Finance. These top DSCR loan companies offer competitive rates, nationwide coverage, flexible terms, and specialized programs for real estate investors. The best lender for you depends on your specific needs, property type, and investment strategy.',
    },
    {
      question: 'What makes a DSCR lender the "best"?',
      answer: 'The best DSCR lenders offer competitive interest rates, flexible underwriting, fast closing times (2-3 weeks), multiple loan products, excellent customer service, and nationwide coverage. Top DSCR loan companies also provide specialized programs like no ratio DSCR loans, portfolio financing, and options for various property types including short-term rentals.',
    },
    {
      question: 'How do I find the best DSCR loan companies?',
      answer: 'To find the best DSCR loan companies, compare rates from multiple lenders, review their minimum requirements (DSCR ratio, credit score, down payment), check customer reviews, evaluate their loan programs, and assess their experience with your property type. Working with a mortgage broker who specializes in DSCR loans can help you access multiple lenders.',
    },
    {
      question: 'Are private DSCR lenders better than banks?',
      answer: 'Private DSCR lenders often offer more flexibility, faster approval, and creative solutions compared to traditional banks. However, banks may provide lower rates for well-qualified borrowers. Private lenders excel at unique situations, lower credit scores, and properties that don\'t fit conventional guidelines. The best choice depends on your specific situation.',
    },
    {
      question: 'What are the top DSCR loan lenders for beginners?',
      answer: 'Top DSCR loan lenders for beginners include Kiavi (tech-friendly platform), Anchor Loans (flexible terms), and RCN Capital (educational resources). These lenders offer guidance for first-time DSCR borrowers, lower minimum experience requirements, and support throughout the loan process. Look for lenders with dedicated investor education and responsive loan officers.',
    },
    {
      question: 'How do national DSCR lenders compare to local lenders?',
      answer: 'National DSCR lenders typically offer standardized programs, competitive rates, and consistent underwriting across all states. Local lenders provide personalized service, market-specific knowledge, and potentially more flexible terms. National lenders are ideal for investors with properties in multiple states, while local lenders excel for market-specific expertise.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <StickyQuoteBar />

      {/* Hero Section - Changed to Deep Emerald/Forest Green */}
      <section className="bg-gradient-to-r from-emerald-900 via-emerald-800 to-teal-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Best <span className="text-yellow-400">DSCR Lenders</span> 2026
            </h1>
            <p className="text-xl text-emerald-100 max-w-3xl mx-auto mb-8">
              Compare top DSCR lenders nationwide. Find competitive rates, flexible terms, and fast approvals from trusted investment property financing experts.
            </p>
            <Link
              to="/apply"
              className="inline-flex items-center justify-center bg-gradient-to-r from-yellow-500 to-yellow-600 text-slate-900 px-8 py-4 rounded-xl font-bold text-lg hover:from-yellow-600 hover:to-yellow-700 transition-all shadow-lg hover:shadow-yellow-500/30 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Get Lender Quotes
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Top DSCR Lenders Comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Top DSCR Loan Lenders Nationwide
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Compare the best DSCR lenders based on rates, terms, and investor reviews
            </p>
          </div>

          <div ref={ref1} className="grid lg:grid-cols-2 gap-8">
            {topLenders.map((lender, index) => (
              <motion.div
                key={lender.name}
                initial={{ opacity: 0, y: 30 }}
                animate={inView1 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-gray-200 hover:border-yellow-500 flex flex-col"
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{lender.name}</h3>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center">
                        <i className="ri-star-fill text-amber-500"></i>
                        <span className="ml-1 font-semibold text-gray-900">{lender.rating}</span>
                      </div>
                      <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold">
                        {lender.rank}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Starting Rate</div>
                    <div className="text-2xl font-bold text-emerald-700">{lender.rate}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Min DSCR</div>
                    <div className="text-2xl font-bold text-emerald-700">{lender.minDSCR}</div>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="text-sm font-semibold text-gray-700 mb-3">{lender.specialty}</div>
                  <ul className="space-y-2">
                    {lender.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <i className="ri-checkbox-circle-fill text-yellow-500 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-auto">
                  <a
                    href={lender.link}
                    className="block w-full px-6 py-3 bg-gradient-to-r from-emerald-800 to-emerald-700 text-white rounded-lg font-bold text-center hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-md hover:shadow-lg hover:scale-105 whitespace-nowrap"
                  >
                    Get Quote from {lender.name}
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lender Types */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Types of DSCR Loan Lenders
            </h2>
            <p className="text-xl text-gray-600">
              Understanding different DSCR lender categories
            </p>
          </div>

          <div ref={ref2} className="grid lg:grid-cols-3 gap-8">
            {lenderTypes.map((type, index) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView2 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-emerald-700 to-emerald-600 rounded-xl mb-6">
                  <i className={`${type.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {type.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {type.description}
                </p>
                <div className="space-y-2">
                  {type.pros.map((pro, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <i className="ri-check-line text-yellow-600"></i>
                      <span className="text-gray-700 text-sm">{pro}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Selection Criteria */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How to Choose the Best DSCR Lender
            </h2>
            <p className="text-xl text-gray-600">
              Key factors to evaluate when comparing DSCR loan companies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {selectionCriteria.map((criteria, index) => (
              <motion.div
                key={criteria.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView2 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-emerald-50/50 rounded-xl p-6 border border-emerald-200"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg mb-4">
                  <i className={`${criteria.icon} text-2xl text-emerald-700`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {criteria.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {criteria.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Best DSCR Lenders FAQs
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about choosing DSCR loan lenders
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 md:p-8 border border-emerald-200 hover:border-yellow-500 transition-all shadow-sm hover:shadow-md"
              >
                <h4 className="text-xl font-bold text-gray-900 mb-3">
                  {faq.question}
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-800 to-teal-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Compare Quotes from Top DSCR Lenders
          </h2>
          <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
            Get personalized quotes from the best DSCR loan companies in minutes. Compare rates, terms, and find the perfect lender for your investment property.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/apply"
              className="inline-flex items-center justify-center bg-gradient-to-r from-yellow-500 to-yellow-600 text-slate-900 px-8 py-4 rounded-xl font-bold text-lg hover:from-yellow-600 hover:to-yellow-700 transition-all shadow-lg hover:shadow-yellow-500/30 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Compare All Lenders
            </Link>
            <a
              href="tel:8005557327"
              className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/20 hover:border-yellow-400 transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (800) 555-DSCR
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
